import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { Tag, TagCategory } from "@/api.types";

// API response interfaces
interface TagsResponse {
  tags: Tag[];
}

interface TagResponse {
  tag: Tag;
}

interface TagCategoriesResponse {
  categories: TagCategory[];
}

interface TagCategoryResponse {
  category: TagCategory;
}

// Interfaces for order tags
interface OrderTagResponse {
  orderTag: {
    id: string;
    orderId: string;
    tagId: string;
    addedBy: string;
    tag: Tag;
    createdAt: string;
    updatedAt: string;
  };
}

interface OrderTagsResponse {
  orderTags: {
    id: string;
    orderId: string;
    tagId: string;
    addedBy: string;
    tag: Tag;
    createdAt: string;
    updatedAt: string;
  }[];
}

// Interfaces for parcel tags
interface ParcelTagResponse {
  parcelTag: {
    id: string;
    parcelId: string;
    tagId: string;
    addedBy: string;
    tag: Tag;
    createdAt: string;
  };
}

interface ParcelTagsResponse {
  parcelTags: {
    id: string;
    parcelId: string;
    tagId: string;
    addedBy: string;
    tag: Tag;
    createdAt: string;
  }[];
}

// Validation schemas for forms
export const tagSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  color: z.string().optional(),
  icon: z.string().optional(),
  type: z.enum(["SYSTEM", "CUSTOMER", "SHARED"]),
  isActive: z.boolean().default(true),
  categoryId: z.string().optional(),
});

export type TagFormValues = z.infer<typeof tagSchema>;

export const tagCategorySchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
});

export type TagCategoryFormValues = z.infer<typeof tagCategorySchema>;

// API hooks for tags
export const useTags = (filters?: {
  type?: string;
  isActive?: boolean;
  categoryId?: string;
}) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["tags", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.type) params.append("type", filters.type);
      if (filters?.isActive !== undefined)
        params.append("isActive", String(filters.isActive));
      if (filters?.categoryId) params.append("categoryId", filters.categoryId);

      const response = await axios.get<TagsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tags?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    enabled: !!token,
  });
};

export const useTagById = (tagId?: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["tags", tagId],
    queryFn: async () => {
      const response = await axios.get<TagResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tags/${tagId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.tag;
    },
    enabled: !!tagId && !!token,
  });
};

export const useCreateTag = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: TagFormValues) => {
      const response = await axios.post<TagResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tags`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tags"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useUpdateTag = (
  tagId: string,
  options?: { onSuccess?: () => void }
) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: Partial<TagFormValues>) => {
      const response = await axios.patch<TagResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tags/${tagId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tags"] });
      void queryClient.invalidateQueries({ queryKey: ["tags", tagId] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useDeleteTag = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (tagId: string) => {
      const response = await axios.delete<{ success: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v1/tags/${tagId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tags"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

// API hooks for tag categories
export const useTagCategories = (filters?: { isActive?: boolean }) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["tag-categories", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.isActive !== undefined)
        params.append("isActive", String(filters.isActive));

      const response = await axios.get<TagCategoriesResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tag-categories?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    enabled: !!token,
  });
};

export const useTagCategoryById = (categoryId?: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["tag-categories", categoryId],
    queryFn: async () => {
      const response = await axios.get<TagCategoryResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tag-categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.category;
    },
    enabled: !!categoryId && !!token,
  });
};

export const useCreateTagCategory = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: TagCategoryFormValues) => {
      const response = await axios.post<TagCategoryResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tag-categories`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tag-categories"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useUpdateTagCategory = (
  categoryId: string,
  options?: { onSuccess?: () => void }
) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: Partial<TagCategoryFormValues>) => {
      const response = await axios.patch<TagCategoryResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tag-categories/${categoryId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tag-categories"] });
      void queryClient.invalidateQueries({
        queryKey: ["tag-categories", categoryId],
      });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useDeleteTagCategory = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const response = await axios.delete<TagCategoryResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/tag-categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tag-categories"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

// API hooks for order tags
export const useOrderTags = (orderId: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["order-tags", orderId],
    queryFn: async () => {
      const response = await axios.get<OrderTagsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/tags`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.orderTags;
    },
    enabled: !!orderId && !!token,
  });
};

export const useAddTagToOrder = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      orderId,
      tagId,
    }: {
      orderId: string;
      tagId: string;
    }) => {
      const response = await axios.post<OrderTagResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/tags`,
        { tagId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["order-tags", variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useRemoveTagFromOrder = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      orderId,
      tagId,
    }: {
      orderId: string;
      tagId: string;
    }) => {
      const response = await axios.delete<{ message: string }>(
        `${import.meta.env.VITE_API_URL}/api/v1/orders/${orderId}/tags/${tagId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["order-tags", variables.orderId],
      });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

// API hooks for parcel tags
export const useParcelTags = (parcelId: string) => {
  const { token } = useSession();

  return useQuery({
    queryKey: ["parcel-tags", parcelId],
    queryFn: async () => {
      const response = await axios.get<ParcelTagsResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/parcels/${parcelId}/tags`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.parcelTags;
    },
    enabled: !!parcelId && !!token,
  });
};

export const useAddTagToParcel = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      parcelId,
      tagId,
    }: {
      parcelId: string;
      tagId: string;
    }) => {
      const response = await axios.post<ParcelTagResponse>(
        `${import.meta.env.VITE_API_URL}/api/v1/parcels/${parcelId}/tags`,
        { tagId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["parcel-tags", variables.parcelId],
      });
      void queryClient.invalidateQueries({ queryKey: ["parcels"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};

export const useRemoveTagFromParcel = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  const { token } = useSession();

  return useMutation({
    mutationFn: async ({
      parcelId,
      tagId,
    }: {
      parcelId: string;
      tagId: string;
    }) => {
      const response = await axios.delete<{ message: string }>(
        `${import.meta.env.VITE_API_URL}/api/v1/parcels/${parcelId}/tags/${tagId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["parcel-tags", variables.parcelId],
      });
      void queryClient.invalidateQueries({ queryKey: ["parcels"] });
      if (options?.onSuccess) options.onSuccess();
    },
  });
};
